/** "What changed" tab — the plan's effect like the publish review: activities
 *  added, removed, and date-moved, grouped by WBS leaf. Driven by the snapshot's
 *  captured `changes` (frozen at publish, since the working DB hard-deletes and
 *  mutates afterward). */
import React from "react";
import type { PullPlanSnapshot, SnapshotChange } from "../../../../models";
import { formatDateMonthDayAbbrev as fmt } from "../../../../utils/format";
import { memberFinish, memberStart } from "../../WbsBoardView/boardLayout";
import "./ChangesTab.css";

const KIND_LABEL: Record<SnapshotChange["kind"], string> = {
  created: "Added",
  deleted: "Removed",
  modified: "Changed",
};

const range = (s: string | null, f: string | null) =>
  `${fmt(s) || "—"} – ${fmt(f) || "—"}`;

const Before: React.FC<{ ch: SnapshotChange }> = ({ ch }) => {
  if (ch.kind === "created") return <span className="cpv-dim">—</span>;
  if (ch.kind === "deleted") {
    return <span>{range(memberStart(ch.member), memberFinish(ch.member))}</span>;
  }
  const s = ch.was?.start !== undefined ? ch.was.start : memberStart(ch.member);
  const f = ch.was?.finish !== undefined ? ch.was.finish : memberFinish(ch.member);
  return <span>{range(s, f)}</span>;
};

const After: React.FC<{ ch: SnapshotChange }> = ({ ch }) => {
  if (ch.kind === "deleted") return <span className="cpv-dim">removed</span>;
  return <span>{range(memberStart(ch.member), memberFinish(ch.member))}</span>;
};

const ChangesTab: React.FC<{ snapshot: PullPlanSnapshot }> = ({ snapshot }) => {
  const changes = React.useMemo(() => snapshot.changes ?? [], [snapshot.changes]);

  const groups = React.useMemo(() => {
    const map = new Map<
      string,
      { label: string; breadcrumb: string[]; rows: SnapshotChange[] }
    >();
    for (const ch of changes) {
      const path = ch.member.wbs_path.length ? ch.member.wbs_path : ["(no WBS node)"];
      const key = path.join(" / ");
      let g = map.get(key);
      if (!g) {
        g = { label: path[path.length - 1], breadcrumb: path.slice(0, -1), rows: [] };
        map.set(key, g);
      }
      g.rows.push(ch);
    }
    return Array.from(map.entries());
  }, [changes]);

  if (changes.length === 0) {
    return (
      <div className="cpv-empty">
        No change detail was captured for this plan
        {snapshot.meta.stale ? " — it was committed before change tracking existed." : "."}
      </div>
    );
  }

  const counts = { created: 0, deleted: 0, modified: 0 };
  for (const c of changes) counts[c.kind] += 1;

  return (
    <div className="cpv-changes">
      <div className="cpv-changes-summary">
        <span className="cpv-chip cpv-chip--add">{counts.created} added</span>
        <span className="cpv-chip cpv-chip--del">{counts.deleted} removed</span>
        <span className="cpv-chip cpv-chip--mod">{counts.modified} changed</span>
      </div>
      {groups.map(([key, g]) => (
        <div key={key} className="cpv-changes-group">
          <div className="cpv-changes-leaf">
            {g.breadcrumb.length > 0 && (
              <span className="cpv-changes-crumb">{g.breadcrumb.join(" › ")} › </span>
            )}
            <span className="cpv-changes-leaf-name">{g.label}</span>
          </div>
          <table className="cpv-changes-table">
            <thead>
              <tr>
                <th>Change</th>
                <th>Code</th>
                <th>Activity</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>
            <tbody>
              {g.rows.map((ch) => (
                <tr key={ch.activity_id} className={`cpv-changes-row cpv-changes-row--${ch.kind}`}>
                  <td>
                    <span className={`cpv-tag cpv-tag--${ch.kind}`}>{KIND_LABEL[ch.kind]}</span>
                  </td>
                  <td className="cpv-changes-code">{ch.member.task_code || "—"}</td>
                  <td className="cpv-changes-name" title={ch.member.task_name}>
                    {ch.member.task_name}
                  </td>
                  <td className="cpv-changes-date"><Before ch={ch} /></td>
                  <td className="cpv-changes-date"><After ch={ch} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default ChangesTab;
